export const GTM_CONTAINER_ID = 'GTM-KL3VRFP'
export const MEASUREMENT_VERSION = '3.0.0'

export const ANALYTICS_EVENTS = Object.freeze({
  PAGE_VIEW: 'page_view',
  NAVIGATION_CLICK: 'navigation_click',
  SELECT_CONTENT: 'select_content',
  CTA_CLICK: 'cta_click',
  CONTACT_CLICK: 'contact_click',
  SOCIAL_CLICK: 'social_click',
  OUTBOUND_CLICK: 'outbound_click',
  SCROLL_DEPTH: 'scroll_depth',
  FORM_START: 'form_start',
  FORM_SUBMIT_ERROR: 'form_submit_error',
  GENERATE_LEAD: 'generate_lead',
  MENU_TOGGLE: 'menu_toggle',
  WHATSAPP_WIDGET_OPEN: 'whatsapp_widget_open',
  WHATSAPP_WIDGET_CLOSE: 'whatsapp_widget_close',
  WHATSAPP_CLICK: 'whatsapp_clic',
})

const createEventId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`

const cleanValue = (value) => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'string') return value.replace(/\s+/g, ' ').trim().slice(0, 160)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  return undefined
}

const currentPageContext = () => {
  if (typeof window === 'undefined') return {}
  return window.__CRS_PAGE_CONTEXT__ || {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    page_language: document.documentElement.lang || 'es-MX',
    site_name: 'CRS Cimentaciones',
  }
}

export function pushAnalyticsEvent(event, parameters = {}) {
  if (typeof window === 'undefined') return null
  window.dataLayer = window.dataLayer || []
  const cleanedParameters = Object.fromEntries(
    Object.entries(parameters)
      .map(([key, value]) => [key, cleanValue(value)])
      .filter(([, value]) => value !== undefined),
  )
  const payload = {
    ...currentPageContext(),
    event,
    event_id: createEventId(),
    event_timestamp: new Date().toISOString(),
    measurement_version: MEASUREMENT_VERSION,
    ...cleanedParameters,
  }
  window.dataLayer.push(payload)
  document.dispatchEvent(new CustomEvent('crs:analytics', { detail: payload }))
  return payload
}

const sha256Hex = async (value) => {
  if (!value || !globalThis.crypto?.subtle) return undefined
  const encoded = new TextEncoder().encode(value)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', encoded)
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

const normalizeEmailForHash = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/\s+/g, '')
  const [localPart, domain] = normalized.split('@')
  if (!localPart || !domain) return ''
  if (domain === 'gmail.com' || domain === 'googlemail.com') return `${localPart.replaceAll('.', '')}@gmail.com`
  return normalized
}

const normalizeNameForHash = (value) => String(value || '').normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ')

const normalizePhoneForHash = (value) => {
  const raw = String(value || '').trim()
  const hasPlus = raw.startsWith('+')
  let digits = raw.replace(/\D/g, '')
  if (!hasPlus && digits.length === 10) digits = `52${digits}`
  if (!hasPlus && digits.startsWith('00')) digits = digits.slice(2)
  const normalized = `+${digits}`
  return /^\+[1-9]\d{10,14}$/.test(normalized) ? normalized : ''
}

export async function buildUserProvidedData({ email, phoneNumber, firstName, lastName, country = 'MX' }) {
  const [emailHash, phoneHash, firstNameHash, lastNameHash] = await Promise.all([
    sha256Hex(normalizeEmailForHash(email)),
    sha256Hex(normalizePhoneForHash(phoneNumber)),
    sha256Hex(normalizeNameForHash(firstName)),
    sha256Hex(normalizeNameForHash(lastName)),
  ])
  if (!emailHash && !phoneHash) return undefined
  return {
    ...(emailHash ? { sha256_email_address: emailHash } : {}),
    ...(phoneHash ? { sha256_phone_number: phoneHash } : {}),
    address: {
      ...(firstNameHash ? { sha256_first_name: firstNameHash } : {}),
      ...(lastNameHash ? { sha256_last_name: lastNameHash } : {}),
      country,
    },
  }
}

export function pushGenerateLeadEvent(parameters, userData) {
  if (typeof window === 'undefined') return null
  window.dataLayer = window.dataLayer || []
  const cleanedParameters = Object.fromEntries(
    Object.entries(parameters)
      .map(([key, value]) => [key, cleanValue(value)])
      .filter(([, value]) => value !== undefined),
  )
  const payload = {
    ...currentPageContext(),
    event: ANALYTICS_EVENTS.GENERATE_LEAD,
    event_id: createEventId(),
    event_timestamp: new Date().toISOString(),
    measurement_version: MEASUREMENT_VERSION,
    ...cleanedParameters,
    ...(userData ? { user_data: userData } : {}),
  }
  window.dataLayer.push(payload)
  document.dispatchEvent(new CustomEvent('crs:analytics', { detail: payload }))
  return payload
}

export function trackWhatsAppClick({ clickLocation, linkText, messageLength, serviceType, source }) {
  return pushAnalyticsEvent(ANALYTICS_EVENTS.WHATSAPP_CLICK, {
    contact_method: 'whatsapp',
    click_location: clickLocation,
    link_text: linkText,
    message_length: messageLength,
    service_type: serviceType,
    interaction_source: source,
  })
}

const inferClickLocation = (element) => element.dataset.analyticsLocation
  || element.closest('[data-analytics-section]')?.dataset.analyticsSection
  || (element.closest('.mobile-panel') ? 'mobile_menu' : null)
  || (element.closest('.site-header') ? 'header' : null)
  || (element.closest('.site-footer') ? 'footer' : null)
  || (element.closest('.article-page') ? 'article' : null)
  || (element.closest('main') ? 'main_content' : 'unknown')

const analyticsData = (element) => ({
  click_location: inferClickLocation(element),
  link_text: element.dataset.analyticsLabel || element.textContent,
  content_type: element.dataset.analyticsContentType,
  content_id: element.dataset.analyticsContentId,
})

const classifyLink = (anchor) => {
  const rawHref = anchor.getAttribute('href') || ''
  let url
  try {
    url = new URL(rawHref, window.location.href)
  } catch {
    return null
  }
  const base = analyticsData(anchor)
  if (/wa\.me|api\.whatsapp\.com/i.test(url.hostname)) {
    return { event: ANALYTICS_EVENTS.WHATSAPP_CLICK, parameters: { click_location: base.click_location, link_text: anchor.dataset.analyticsLabel, contact_method: 'whatsapp', interaction_source: 'direct_link' } }
  }
  if (url.protocol === 'tel:' || url.protocol === 'mailto:') {
    return { event: ANALYTICS_EVENTS.CONTACT_CLICK, parameters: { click_location: base.click_location, link_text: anchor.dataset.analyticsLabel, contact_method: url.protocol === 'tel:' ? 'phone' : 'email' } }
  }
  if (['www.linkedin.com', 'linkedin.com', 'www.instagram.com', 'instagram.com'].includes(url.hostname)) {
    return { event: ANALYTICS_EVENTS.SOCIAL_CLICK, parameters: { ...base, social_network: url.hostname.includes('linkedin') ? 'linkedin' : 'instagram', link_url: `${url.origin}${url.pathname}` } }
  }
  if (url.origin !== window.location.origin) {
    return { event: ANALYTICS_EVENTS.OUTBOUND_CLICK, parameters: { ...base, link_domain: url.hostname, link_url: `${url.origin}${url.pathname}` } }
  }
  return { event: ANALYTICS_EVENTS.NAVIGATION_CLICK, parameters: { ...base, link_url: url.pathname + url.hash } }
}

export function setupInteractionTracking() {
  if (typeof window === 'undefined') return () => {}
  const trackedDepths = new Set()
  const depths = [25, 50, 75, 90]

  const handleClick = (event) => {
    const element = event.target.closest('a, button')
    if (!element || element.dataset.analyticsManual === 'true') return
    const explicitEvent = element.dataset.analyticsEvent
    if (explicitEvent) {
      pushAnalyticsEvent(explicitEvent, analyticsData(element))
      return
    }
    if (element.tagName !== 'A') return
    const classified = classifyLink(element)
    if (classified) pushAnalyticsEvent(classified.event, classified.parameters)
  }

  const handleScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    if (scrollable <= 0) return
    const percent = Math.round((window.scrollY / scrollable) * 100)
    for (const depth of depths) {
      if (percent >= depth && !trackedDepths.has(depth)) {
        trackedDepths.add(depth)
        pushAnalyticsEvent(ANALYTICS_EVENTS.SCROLL_DEPTH, { percent_scrolled: depth })
      }
    }
  }

  document.addEventListener('click', handleClick)
  window.addEventListener('scroll', handleScroll, { passive: true })
  return () => {
    document.removeEventListener('click', handleClick)
    window.removeEventListener('scroll', handleScroll)
  }
}
